import { useNav } from '../app/ui';
import { useState } from 'react';
import type { Aspect } from '@noorview/core';
import { TEMPLATES, applyTemplate, blankDesign } from '@noorview/core';
import { attempt, backend, demoMode, openVirtualScreen, toast, useApp } from '../data/store';
import { Segmented, Sheet } from '../ui/controls';
import { Icon } from '../ui/icons';
import { Select } from '../ui/pickers';
import { TextField } from '../ui/fields';
import { newDisplayPath } from '../app/displaySlot';

function CodeInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <label className="field"><span>Code shown on the display</span>
      <input className="input code-input mono" value={value} maxLength={7} placeholder="ABC123" autoComplete="off" spellCheck={false} autoCapitalize="characters"
        onChange={(e) => onChange(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))} aria-describedby="code-help" />
      <small id="code-help" className="hint">Open NoorView on the screen. It shows a 6-character code while it waits to be paired.</small>
    </label>
  );
}

function VirtualScreenHint() {
  const [copied, setCopied] = useState(false);
  if (backend.kind !== 'sandbox') {
    // Until the TV app is ready, any browser can act as a display: this window, or another computer or TV.
    // A real TV opens /display (it stays that display for good). The link and the test screen ask for a new display, so
    // each one opened gets its own new code, even in a browser that's already another screen.
    const url = `${location.origin}/display`;
    const link = `${location.origin}${newDisplayPath}`;
    const copy = () => { void navigator.clipboard?.writeText(link).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }); };
    return (
      <div className="banner info pair-tip">
        <Icon name="tv" size={20} />
        <span>No TV app yet? Open <b>{url.replace(/^https?:\/\//, '')}</b> on any computer or TV browser. It shows a code to pair.</span>
        <div className="pair-tip-actions">
          <button type="button" className="btn sm plain" onClick={copy}>{copied ? 'Copied' : 'Copy link'}</button>
          <button type="button" className="btn sm primary" onClick={() => window.open(newDisplayPath, '_blank', 'popup,width=1280,height=760')}>Open test screen</button>
        </div>
      </div>
    );
  }
  return (
    <div className="banner info pair-tip">
      <Icon name="tv" size={20} />
      <span>{demoMode ? 'Demo: real TVs can’t be paired here. Open a virtual screen in a new window to get a code.' : 'Sandbox: open a virtual screen in a new window to get a code.'}</span>
      <div className="pair-tip-actions"><button type="button" className="btn sm primary" onClick={() => openVirtualScreen()}>Open virtual screen</button></div>
    </div>
  );
}

export function PairSheet({ screenId, onClose }: { screenId?: string; onClose: () => void }) {
  const ws = useApp((s) => s.ws)!;
  const [screen, setScreen] = useState(screenId ?? ws.screens.find((s) => !s.deviceId && !s.locked)?.id ?? ws.screens.find((s) => !s.locked)?.id ?? ws.screens[0]?.id ?? '');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const target = ws.screens.find((s) => s.id === screen);
  const submit = async () => {
    setBusy(true);
    const ok = await attempt(() => backend.pairScreen(code, screen), `${target?.name ?? 'Screen'} paired`);
    setBusy(false);
    if (ok !== undefined) onClose();
  };
  return (
    <Sheet open onClose={onClose} title="Pair a display" subtitle="Connect a TV or screen to one of your screens."
      footer={<><button type="button" className="btn plain" onClick={onClose}>Cancel</button><button type="button" className="btn primary" disabled={busy || code.length !== 6 || !screen || !!target?.locked} onClick={submit}>{busy ? 'Pairing…' : 'Pair'}</button></>}>
      <VirtualScreenHint />
      <div className="field"><span>Screen</span>
        <Select label="Screen" value={screen} onChange={setScreen} options={ws.screens.map((s) => ({ value: s.id, label: s.name, hint: s.locked ? 'Locked' : s.deviceId ? 'Replaces its current display' : undefined, disabled: s.locked && s.id !== screen }))} />
        {target?.locked && <small className="hint">{target.name} is locked. Unlock it on the Screens page to pair a display.</small>}
      </div>
      <CodeInput value={code} onChange={setCode} />
    </Sheet>
  );
}

export function AddScreenSheet({ onClose }: { onClose: () => void }) {
  const ws = useApp((s) => s.ws)!;
  const nav = useNav();
  const [name, setName] = useState('');
  const [aspect, setAspect] = useState<Aspect>('16:9');
  const [template, setTemplate] = useState('classic');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const taken = ws.screens.some((s) => s.name.trim().toLowerCase() === name.trim().toLowerCase());
  const submit = async (edit: boolean) => {
    if (!name.trim() || taken) return;
    setBusy(true);
    const t = TEMPLATES.find((x) => x.id === template);
    const design = t ? applyTemplate(blankDesign(aspect), t) : blankDesign(aspect);
    const s = await attempt(() => backend.createScreen({ name: name.trim(), design }));
    const paired = s && code.length === 6 ? await attempt(() => backend.pairScreen(code, s.id)) : undefined;
    // Fetch the masjid again first, so the screen is in the list (and the Studio opens straight onto it).
    if (s) await useApp.getState().reload();
    setBusy(false);
    if (!s) return;
    toast(paired ? `${s.name} added and paired` : edit ? `${s.name} added. Design it, then click Send Live.` : `${s.name} added. Pair a display from Screens when you’re ready.`);
    onClose();
    if (edit) nav(`/studio/${s.id}`);
  };
  return (
    <Sheet open onClose={onClose} title="Add Screen" subtitle="Name it after where it hangs, so everyone knows which is which."
      footer={<><button type="button" className="btn plain" disabled={busy || !name.trim() || taken} onClick={() => submit(false)}>Add</button><button type="button" className="btn primary" disabled={busy || !name.trim() || taken} onClick={() => submit(true)}>Add and design</button></>}>
      <TextField label="Name" value={name} onChange={setName} max={40} placeholder="e.g. Main Hall, Corridor, Sisters’ Hall" hint={taken ? 'You already have a screen with that name.' : undefined} />
      <div className="field"><span>Orientation</span>
        <Segmented label="Orientation" value={aspect} onChange={setAspect} options={[{ value: '16:9', label: 'Landscape' }, { value: '9:16', label: 'Portrait' }, { value: '21:9', label: 'Ultrawide' }, { value: '4:3', label: '4:3' }]} />
      </div>
      <div className="field"><span>Start from</span>
        <Select label="Start from" value={template} onChange={setTemplate} options={[{ value: '', label: 'Blank canvas' }, ...TEMPLATES.map((t) => ({ value: t.id, label: `${t.name} layout` }))]} />
      </div>
      <VirtualScreenHint />
      <CodeInput value={code} onChange={setCode} />
      <p className="hint">No code yet? Add the screen now and pair a display later from Screens.</p>
    </Sheet>
  );
}
